// Unpublished Work © 2022-2026 Deere & Company.

import PropTypes from 'Utils/prop-type-utils';
import React from 'react';
import {BY_USER_STATUS} from 'OnLabor/report/constants/labor-report';
import {dataTableFormatTime} from 'Utils/time-utils';
import {formatNumber} from 'Utils/unit-conversion-utils';
import {isAggregationFooterRow} from 'Ui/components/common/data-table/data-grid-utils';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';

function getColumns({
    dateSelector,
    secondarySelector,
    tertiarySelector,
    timeScale,
    translations
}) {
    const formattedDate = dataTableFormatTime(dateSelector, timeScale);

    return [
        {
            headerName: secondarySelector === BY_USER_STATUS ? translations.STATUS : translations.ONLINK_EMPLOYEE,
            field: 'name',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                if (isAggregationFooterRow(params)) {
                    return `${formattedDate} ${translations.ONLINK_TOTAL} - ${tertiarySelector}`;
                }

                return params.value;
            }
        },
        {
            headerName: translations.ONLINK_TOTAL,
            field: 'total',
            minWidth: 150,
            flex: 1,
            type: 'number',
            valueFormatter: (value) => formatNumber(value)
        }
    ];
}

function getRows(laborReportData, secondarySelector, tertiarySelector) {
    if (secondarySelector === BY_USER_STATUS) {
        const user = laborReportData?.find((user) => user.name === tertiarySelector);

        return user ? Object.keys(user.optionMap).map((key) => ({
            name: key,
            total: user.optionMap[key]
        })) : [];
    }

    return laborReportData.reduce((data, user) => {
        const {
            name,
            optionMap
        } = user;

        if (optionMap[tertiarySelector]) {
            data.push({
                name,
                total: optionMap[tertiarySelector]
            });
        }

        return data;
    }, []);
}

function ScheduleStatusDataTable(props) {
    const {
        dateSelector,
        laborReportData,
        secondarySelector,
        tertiarySelector,
        timeScale,
        translations
    } = props;

    const rows = getRows(laborReportData, secondarySelector, tertiarySelector);

    return (
        <div className={'ops-center-table-container'}>
            <DataGridWrapper
                aggregation={{
                    name: {
                        type: 'size',
                        config: {}
                    },
                    total: {
                        type: 'sum',
                        config: {}
                    }
                }}
                columns={getColumns({
                    dateSelector,
                    rows,
                    secondarySelector,
                    tertiarySelector,
                    timeScale,
                    translations
                })}
                getRowId={(row) => row.name ?? translations.ONLINK_TOTAL}
                pinnedColumns={{
                    left: [],
                    right: []
                }}
                rows={rows}
                slotProps={{
                    root: {
                        disableColumnMenu: true
                    }
                }}
                translations={translations}
            />
        </div>
    );
}

ScheduleStatusDataTable.propTypes = {
    dateSelector: PropTypes.string,
    laborReportData: PropTypes.arrayOf(PropTypes.object),
    secondarySelector: PropTypes.string,
    tertiarySelector: PropTypes.string,
    timeScale: PropTypes.string,
    translations: PropTypes.translations
};

export default ScheduleStatusDataTable;
